import { useEffect, useRef, useState } from 'react';
import { message, PageHeader, Dropdown, Menu, Skeleton, Select } from 'antd';
import { QrcodeOutlined, SettingOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet';

import SurveysService from './SurveysService';
import styles from './surveyswizardpage.module.css';
import AuthService from '../auth/AuthService';
import QRDownloadModal from './components/QRDownloadModal';
import { useParams, useNavigate } from 'react-router-dom';
import QRAdminModal from './components/QRAdminModal';
import ClientsService from '../clients/ClientsService';

interface SurveysWizardProps {
    noBack?: boolean;
}

const DEFAULT_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 493.508 493.508"><path d="M421.38 72.426C374.82 25.786 312.86.07 246.736.006 110.804.006.116 110.578 0 246.506c-.064 65.916 25.548 127.904 72.1 174.56 46.568 46.628 108.284 72.436 174.196 72.436h.572v-.024c134.956 0 246.54-110.596 246.64-246.516.064-65.912-25.584-127.892-72.128-174.536zm-163.68 304.7c-2.836 2.864-6.8 4.504-10.872 4.504-4.088-.008-8.064-1.656-10.932-4.56-2.848-2.824-4.484-6.792-4.48-10.876.008-4.072 1.664-8.064 4.532-10.924 2.856-2.848 7.064-4.488 10.92-4.488 4.06 0 8.028 1.648 10.86 4.504 2.896 2.92 4.556 6.916 4.548 10.932-.004 4.032-1.672 8.004-4.576 10.908zm7.424-94.452a3.854 3.854 0 00-3.024 3.752l-.02 18.12c-.008 8.488-6.916 15.408-15.436 15.408-8.512-.008-15.412-6.94-15.404-15.436l.036-34.964c.008-8.484 6.916-15.08 15.416-15.08h.068c29.488 0 53.5-24.32 53.532-53.808.012-14.288-5.536-27.884-15.64-38.024-10.14-10.132-23.568-15.788-37.884-15.804-29.492 0-53.512 23.956-53.544 53.464-.008 8.492-6.924 15.38-15.44 15.38a15.29 15.29 0 01-10.9-4.544c-2.916-2.912-4.508-6.788-4.504-10.904.044-46.492 37.896-84.324 84.448-84.324 46.532.048 84.344 37.936 84.3 84.46-.032 39.172-27.78 73.788-66.004 82.304z"/></svg>';

const SurveysWizardPage = ({ noBack }: SurveysWizardProps) => {
    const { client } = useParams() as { client: string };
    const navigate = useNavigate();

    const filtersRef = useRef<any>();

    const [clientName, setClientName] = useState('');
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<any>();
    const [loadingFilters, setLoadingFilters] = useState(false);
    const [organizationalUnits, setOrganizationalUnits] = useState<any[]>();
    const [surveys, setSurveys] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [categoriesAll, setCategoriesAll] = useState<any[]>([]);
    const [selectedOU, setSelectedOU] = useState<any>();
    const [selectedSurvey, setSelectedSurvey] = useState<any>();
    const [selectedValue, setSelectedValue] = useState<string>();
    const [modalVisible, setModalVisible] = useState(false);
    const [adminModalVisible, setAdminModalVisible] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');

    useEffect(() => {
        const queryOUs = async () => {
            try {
                setLoading(true);
                const data = await ClientsService.getOrganizationalUnits(
                    client
                );
                console.log('ous are', data);
                setOrganizationalUnits(data);
            } catch (err) {
                console.error(err);
                message.error((err as Error).message);
            }

            setLoading(false);
        };
        queryOUs();
    }, [client]);

    useEffect(() => {
        const querySurveys = async () => {
            try {
                setLoading(true);
                const data = await SurveysService.getSurveys(
                    client,
                    '',
                    'true',
                    1,
                    1000,
                    selectedOU?.id
                );
                let categories = await SurveysService.getCategories();
                setCategoriesAll(categories);
                categories = categories.filter(
                    (c: any) =>
                        !data.results.some((d: any) => d.category_id === c.id)
                );

                setCategories(categories);
                setSurveys(data.results);
                setClientName(data.metadata.client);
            } catch (err) {
                console.error(err);
                message.error((err as Error).message);
            }

            setLoading(false);
        };
        if (
            selectedOU ||
            (organizationalUnits !== undefined &&
                organizationalUnits.length === 0)
        ) {
            querySurveys();
        }
    }, [client, organizationalUnits, selectedOU]);

    useEffect(() => {
        const queryFilters = async () => {
            try {
                if (!selectedSurvey) return;
                setLoadingFilters(true);
                const tags = await SurveysService.getSurveyTags(
                    client,
                    selectedSurvey.id
                );
                const filter = tags.find((t: any) => t.main_tag);
                if (!filter) {
                    setFilter(false);
                    setLoadingFilters(false);
                    return;
                }
                filter.values.unshift('');
                setFilter(filter);
                filtersRef.current.scrollIntoView({ behavior: 'smooth' });
            } catch (err) {
                console.error(err);
                message.error((err as Error).message);
            }

            setLoadingFilters(false);
        };
        queryFilters();
    }, [client, selectedSurvey]);

    const onCategoryChanged = (val: string) => {
        setSelectedValue(undefined);
        setFilter(undefined);
        setSelectedSurvey(undefined);
        setSelectedCategory(val);
    };

    const onSurveySelected = (s: any) => {
        setSelectedSurvey(s);
    };

    const onOrganizationalUnitSelected = (ou: any) => {
        setSelectedOU(ou);
    };

    const renderOU = (ou: any) => {
        return (
            <div
                key={ou.id}
                className={`${styles.ouItem} ${
                    ou === selectedOU ? styles.selected : ''
                }`}
                title={ou.name}
                onClick={(e) => onOrganizationalUnitSelected(ou)}
            >
                <div
                    dangerouslySetInnerHTML={{
                        __html: ou.icon ?? DEFAULT_ICON,
                    }}
                />
                <div>{ou.name}</div>
            </div>
        );
    };

    const renderSurvey = (s: any) => {
        return (
            <div
                key={s.id}
                className={`${styles.buttonItem} ${
                    s === selectedSurvey ? styles.selected : ''
                }`}
                title={s.title}
                onClick={(e) => onSurveySelected(s)}
            >
                <div
                    dangerouslySetInnerHTML={{ __html: s.icon ?? DEFAULT_ICON }}
                />
                <div>{s.title}</div>
            </div>
        );
    };

    const renderCategory = (c: any) => {
        return (
            <div
                key={c.id}
                className={`${styles.buttonItem} ${styles.disabled}`}
                title={c.description}
            >
                <div dangerouslySetInnerHTML={{ __html: c.icon }} />
                <div>{c.name}</div>
            </div>
        );
    };

    const renderFilter = (f: any) => {
        return (
            <div
                key={f}
                className={`${styles.buttonItem} ${
                    f === selectedValue ? styles.selected : ''
                }`}
                onClick={(e) => setSelectedValue(f)}
            >
                <div>{f || 'Todos'}</div>
            </div>
        );
    };

    const renderCategories = () => {
        return (
            <Select
                style={{ minWidth: 200 }}
                value={selectedCategory}
                onChange={onCategoryChanged}
                optionLabelProp="label"
            >
                <Select.Option label={`Categoría: Todos`} value="">
                    Todos
                </Select.Option>
                {categoriesAll.map((c) => (
                    <Select.Option
                        key={c.id}
                        label={`Categoría: ${c.name}`}
                        value={c.id}
                    >
                        {c.name}
                    </Select.Option>
                ))}
            </Select>
        );
    };

    const openQRDownloadModal = () => {
        setModalVisible(true);
    };

    const closeQRModal = () => {
        setModalVisible(false);
    };

    const openAdminModal = () => {
        setAdminModalVisible(true);
    };

    const closeAdminModal = () => {
        setAdminModalVisible(false);
    };

    const hasOUs = () => {
        return !!organizationalUnits?.length;
    };

    const title = clientName
        ? `Evaluaciones de ${clientName}${
              selectedOU ? ` - ${selectedOU.name}` : ''
          }`
        : 'Evaluaciones';

    const onShowResults = () => {
        navigate(
            `${selectedSurvey?.uuid}/results`,
            filter
                ? {
                      state: {
                          [filter.name]: selectedValue,
                      },
                  }
                : undefined
        );
    };

    return (
        <div className={styles.wizard}>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <img
                src={SurveysService.getImagePath(
                    client || AuthService.getClientSlug()
                )}
                alt="logo"
                className={styles.logo}
            />
            {!!organizationalUnits?.length && (
                <div className={styles.ous}>
                    {organizationalUnits.map(renderOU)}
                </div>
            )}
            {(!hasOUs() || selectedOU) && (
                <PageHeader
                    backIcon={!!noBack ? false : undefined}
                    title={title}
                    extra={[renderCategories()]}
                ></PageHeader>
            )}

            <div className={styles.section}>
                {loading
                    ? Array(5)
                          .fill(0)
                          .map((k, i) => (
                              <Skeleton.Button
                                  key={i}
                                  block
                                  active
                                  shape="round"
                              />
                          ))
                    : (surveys || [])
                          .filter(
                              (c) =>
                                  selectedCategory === '' ||
                                  c.category_id === selectedCategory
                          )
                          .map(renderSurvey)
                          .concat(
                              (categories || [])
                                  .filter(
                                      (c) =>
                                          selectedCategory === '' ||
                                          c.id === selectedCategory
                                  )
                                  .map(renderCategory)
                          )}
            </div>
            {!!selectedSurvey && (
                <>
                    {loadingFilters ? (
                        <Skeleton
                            title={{ width: '120px' }}
                            paragraph={false}
                        />
                    ) : (
                        <h3>{(filter || { name: '' }).name}</h3>
                    )}
                    <div ref={filtersRef} className={styles.section}>
                        {loadingFilters
                            ? Array(5)
                                  .fill(0)
                                  .map((k, i) => (
                                      <Skeleton.Button
                                          key={i}
                                          block
                                          active
                                          shape="round"
                                      />
                                  ))
                            : (filter || { values: [] }).values.map(
                                  renderFilter
                              )}
                    </div>
                </>
            )}

            {(selectedValue !== undefined || filter === false) && (
                <>
                    <div className={styles.actions}>
                        <Dropdown.Button
                            onClick={onShowResults}
                            type="primary"
                            size="large"
                            overlay={
                                <Menu
                                    onClick={(e) =>
                                        e.key === '1'
                                            ? openQRDownloadModal()
                                            : openAdminModal()
                                    }
                                >
                                    <Menu.Item key="1">
                                        <QrcodeOutlined /> Descargar códigos QR
                                    </Menu.Item>
                                    <Menu.Item key="2">
                                        <SettingOutlined /> Administrar Códigos
                                    </Menu.Item>
                                </Menu>
                            }
                        >
                            Ver resultados
                        </Dropdown.Button>
                    </div>
                    <QRDownloadModal
                        visible={modalVisible}
                        survey={selectedSurvey}
                        client={client}
                        filters={
                            filter
                                ? { [filter.name]: selectedValue }
                                : undefined
                        }
                        onCancel={closeQRModal}
                        onOk={closeQRModal}
                    />

                    <QRAdminModal
                        visible={adminModalVisible}
                        survey={selectedSurvey}
                        client={client}
                        filters={
                            filter
                                ? { [filter.name]: selectedValue }
                                : undefined
                        }
                        onCancel={closeAdminModal}
                        onOk={closeAdminModal}
                    />
                </>
            )}
        </div>
    );
};

export default SurveysWizardPage;
